import { useState, useEffect } from "react";
import axios from "axios";
import dayjs from 'dayjs';
import { Auth } from 'aws-amplify';
import { useAuth } from '../../utils/hooks/use-auth';
import { AchievementDownloadReport, AchievementDownloadReportRecord, SearchCondition } from '../interfaces/AchievementDownloadReport'
 
type DownloadReport<T> = {
  page: number,
  searchedData: T[],
  curPageData: T[],
  setCurPageData: (data: T[]) => void;
  getDownloadList:  () => Promise<T[] | string>;
  targetDateList: string[],
  setSearchConditionTargetDate: (startDate?: string, endDate?: string) => void;
  setSearchConditionName: (name?: string[]) => void;
  nameList: string[];
  setSearchConditionContractId: (contractId?: string[]) => void;
  contractIdList: string[];
  setSearchConditionSupplyPointNo: (supplyPointNo?: string[]) => void;
  supplyPointNoList: string[];
  setSearchConditionAddress: (address?: string[]) => void;
  addressList: string[];
  setSearchConditionContractTypeName: (contractTypeName?: string[]) => void;
  contractTypeNameList: string[];
  setSearchConditionVoltageClass: (low?: boolean, high?: boolean, exHigh?: boolean) => void;
  setSearchConditionSupplyDate: (startHead: Date | null, startTail: Date | null, EndHead: Date | null, EndTail: Date | null) => void;
  resetState: boolean;
  resetData: () => void;
  clearResetSatate: () => void;
  pagenation: (curPage: number) => void;
  allCheck: (state: boolean) => void;
  singleCheck: (target_ym: string, name: string) => void;
  initTargetMonth: () => void;
  searchCondition: SearchCondition;
};

// 検索条件の初期値
const initSerchVoltageClass = {
  exHigh: true,
  high: true,
  low: true
};
const initSupplyDate = {
  head: (new Date(1000, 1 ,15, 0, 0, 0)),
  tail: (new Date(9998, 12, 15, 0, 0, 0))
}
const initSerchCondition = {
  targetMonth: "",
  name: [],
  contractId: [],
  supplyPointNo: [],
  contractTypeName: [],
  address: [],
  SearchVoltageClass: initSerchVoltageClass,
  supplyStartDate: initSupplyDate,
  supplyEndDate: initSupplyDate
}

const resetSearchCondition = {
  requestPointName: [],
  requestPointId: [],
  supplyPointNo: [],
  feeMenuNamePrinting: [],
  address: [],
  SearchVoltageClass: initSerchVoltageClass,
  supplyStartDate: initSupplyDate,
  supplyEndDate: initSupplyDate
}

// APIリクエストのベースURL
const baseURL = process.env.REACT_APP_API_ENDPOINT;
// 1ページ当たりに表示するデータ件数
const itemsPerPage: number = Number(process.env.REACT_APP_INVOICE_ITEMS_PER_PAGE) || 5;

// トークン取得
const getCurrentUserToken = async () => {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch (error) {
    console.log(error);
  }
};

// メインフック
export function useAchievementDonwloadReport(): DownloadReport<AchievementDownloadReportRecord> {
  const auth = useAuth();
  // データを格納する変数
  const [defaultData, setDefaultData] = useState<AchievementDownloadReportRecord[]>([]);
  const [searchedData, setSearchedData] = useState<AchievementDownloadReportRecord[]>([]);
  const [curPageData, setCurPageData] = useState<AchievementDownloadReportRecord[]>([]);
  // 検索条件を格納する変数
　const [searchCondition, setSearchCondition] = useState<SearchCondition>({...initSerchCondition});
　const [targetDateList, setTargetDateList] = useState<string[]>([]);  // 対象年月
  const [nameList, setNameList] = useState<string[]>([]);
  const [contractIdList, setContractIdList] = useState<string[]>([]);
  const [supplyPointNoList, setSupplyPointNoList] = useState<string[]>([]);
  const [addressList, setAddressList] = useState<string[]>([]);
  const [contractTypeNameList, setContractTypeNameList] = useState<string[]>([]);
  const [supplyStartDate, setSupplyStartDate] = useState<Date | null>(null);
  const [supplyEndtDate, setSupplyEndDate] = useState<Date | null>(null);
  // 検索条件の初期化を制御する変数
  const [resetState, setResetState] = useState<boolean>(false);
  // ページ番号を保持する変数
  const [page, setPage] = useState<number>(1);
  // 契約名を保持する変数
  let contractName = "";
  
  
  useEffect(() => {
    if (defaultData.length > 0) searchData();
    getDropdownSearchList();
  }, [defaultData]);
  
  useEffect(() => {
    searchData();
    pagenation(1);
  }, [searchCondition]);
  
  const initTargetMonth = () => {
    const startDate = dayjs('201901');
    const endDate = dayjs();
    
    // 各月を格納する配列を初期化
    const monthsArray: string[] = [];
    
    // 開始日から終了日までループを回して、各月を配列に追加
    let currentDate = startDate;
    while (currentDate.isBefore(endDate, 'month') || currentDate.isSame(endDate, 'month')) {
      // フォーマット YYYY/MM で配列に追加
      monthsArray.push(currentDate.format('YYYY/MM'));
      // 次の月へ
      currentDate = currentDate.add(1, 'month');
    }
    
    setSearchCondition({...searchCondition, targetMonth: String(endDate.format('YYYYMM'))});
    setTargetDateList([...monthsArray]);
  }
  
  const setSearchConditionTargetDate = (month: string = "") => {
    if(month === ""){
      setSearchCondition({...searchCondition, targetMonth: dayjs().format('YYYYMM')});
    }
    else{
      setSearchCondition({...searchCondition, targetMonth: dayjs(month).format('YYYYMM')});
    }
  }
  
  const setSearchConditionName = (name: string[] = []) => {
    if(name.length !== 0){
      setSearchCondition({...searchCondition, name: [...name]});
    }
    else{
      setSearchCondition({...searchCondition, name: []});
    }
  }

  const setSearchConditionContractId = (contractId: string[] = []) => {
    if(contractId.length !== 0){
      setSearchCondition({...searchCondition, contractId: contractId});
    }
    else{
      setSearchCondition({...searchCondition, contractId: []});
    }
  }

  const setSearchConditionSupplyPointNo = (supplyPointNo: string[] = []) => {
    if(supplyPointNo.length !== 0){
      setSearchCondition({...searchCondition, supplyPointNo: supplyPointNo});
    }
    else{
      setSearchCondition({...searchCondition, supplyPointNo: []});
    }
  }
  
  const setSearchConditionAddress = (address: string[] = []) => {
    if(address.length !== 0){
      setSearchCondition({...searchCondition, address: address});
    }
    else{
      setSearchCondition({...searchCondition, address: []});
    }
  }
  
  const setSearchConditionContractTypeName = (contractTypeName: string[] = []) => {
    if(contractTypeName.length !== 0){
      setSearchCondition({...searchCondition, contractTypeName: contractTypeName});
    }
    else{
      setSearchCondition({...searchCondition, contractTypeName: []});
    }
  }
  
  const setSearchConditionVoltageClass = (exHigh: boolean = false, high: boolean = false, low: boolean = false) => {
    const targetVoltageClass = {
      exHigh,
      high,
      low
    };
    setSearchCondition({...searchCondition, SearchVoltageClass: targetVoltageClass});
  }
  const setSearchConditionSupplyDate = (startHead: Date|null = null, startTail: Date|null = null, EndHead: Date|null = null, EndTail: Date|null = null ) => {
    setSearchCondition({...searchCondition, supplyStartDate: {head: startHead, tail: startTail}, supplyEndDate: {head: EndHead, tail: EndTail}});

    if(startHead !== null){
      setSearchCondition({...searchCondition, supplyStartDate: {...searchCondition.supplyStartDate, head: startHead}});
    }
    if(startTail !== null){
      setSearchCondition({...searchCondition, supplyStartDate: {...searchCondition.supplyStartDate, tail: startTail}});
    }
    if(EndHead !== null){
      setSearchCondition({...searchCondition, supplyEndDate: {...searchCondition.supplyEndDate, head: EndHead}});
    }
    if(EndTail !== null){
      setSearchCondition({...searchCondition, supplyEndDate: {...searchCondition.supplyEndDate, tail: EndTail}});
    }
  }
  
  // 検索処理のメイン関数
  const searchData = () => {
    let searchedData1: AchievementDownloadReportRecord[] = [];
    let searchedData2: AchievementDownloadReportRecord[] = [];
  
    searchedData1 = defaultData;
  
    /*需要場所による検索処理*/
    if(searchCondition.name.length !== 0){
      searchedData1.forEach((item, index) => {
        if(searchCondition.name.indexOf(item.name) !== -1){
          searchedData2.push(searchedData1[index]);
        }
      });
    }
    else{
      searchedData2 = [...searchedData1];
    }
    searchedData1 = [];
  
    /*需要場所IDによる検索処理*/
    if(searchCondition.contractId.length !== 0){
      searchedData2.forEach((item, index) => {
        if(searchCondition.contractId.indexOf(item.contract_id) !== -1){
          searchedData1.push(searchedData2[index]);
        }
      });
    }
    else{
      searchedData1 = [...searchedData2];
    }
    searchedData2 = [];
    
    /*住所による検索処理*/
    if(searchCondition.address.length !== 0){
      searchedData1.forEach((item, index) => {
        if(item.address != null && searchCondition.address.length > 0){
          if(searchCondition.address.indexOf(item.address) !== -1){
            searchedData2.push(searchedData1[index]);
          }
        }else if(!item.address){
        }else{
          searchedData2.push(searchedData1[index]);
        }
      });
    }
    else{
      searchedData2 = [...searchedData1];
    }
    searchedData1 = [];
    
    /*供給地点番号による検索処理*/
    if(searchCondition.supplyPointNo.length !== 0){
      searchedData2.forEach((item, index) => {
        if(item.supply_point_no != null && searchCondition.supplyPointNo.length > 0){
          if(searchCondition.supplyPointNo.indexOf(item.supply_point_no) !== -1){
            searchedData1.push(searchedData2[index]);
          }
        }else if(!item.supply_point_no){
        }else{
          searchedData1.push(searchedData2[index]);
        }
      });
    }
    else{
      searchedData1 = [...searchedData2];
    }
    searchedData2 = [];
    
    /*契約種別による検索処理*/
    if(searchCondition.contractTypeName.length !== 0){
      searchedData1.forEach((item, index) => {
        if(item.contract_type_name != null && searchCondition.contractTypeName.length > 0){
          if(searchCondition.contractTypeName.indexOf(item.contract_type_name) !== -1){
            searchedData2.push(searchedData1[index]);
          }
        }else if(!item.contract_type_name){
        }else{
          searchedData2.push(searchedData1[index]);
        }
      });
    }
    else{
      searchedData2 = [...searchedData1];
    }
    searchedData1 = [];

    /* ダウンロード対象ファイルによる検索処理 */
    searchedData2.forEach((item, index) => {
      let pushFlag = false;
      if(searchCondition.SearchVoltageClass.exHigh && item.voltage_class === "特高"){
        pushFlag = true;
      }
      else if(searchCondition.SearchVoltageClass.high && item.voltage_class === "高圧"){
        pushFlag = true;
      }
      else if(searchCondition.SearchVoltageClass.low && item.voltage_class === "低圧"){
        pushFlag = true;
      }
      else if(searchCondition.SearchVoltageClass.exHigh && searchCondition.SearchVoltageClass.high && searchCondition.SearchVoltageClass.low && item.voltage_class == null){
        pushFlag = true;
      }
      if (pushFlag) searchedData1.push(searchedData2[index]);
    });
    
    /* 供給開始日による検索処理 */
    searchedData1 = searchSupplyStartDate(searchCondition.supplyStartDate.head, searchCondition.supplyStartDate.tail);
    /* 供給終了日による検索処理 */
    searchedData1 = searchSupplyEndDate(searchCondition.supplyEndDate.head, searchCondition.supplyEndDate.tail);

    setSearchedData([...searchedData1]);

    // 対象年月検索のメインロジック関数
    function setResultData(s: Date, e: Date){
      let tmpData: AchievementDownloadReportRecord[] = [];
      defaultData.forEach((obj, i) => {
        const y: number = Number(obj.target_ym.slice(0, 4));
        const m: number = Number(obj.target_ym.slice(4, 6));
        const tmpDateObj: Date = new Date(y, m, 15, 0, 0, 0);
        if(tmpDateObj >= s && tmpDateObj <= e) tmpData.push(defaultData[i]);
      })
      return tmpData
    }

    function searchSupplyStartDate(s: any, e: any){
      let tmpData: AchievementDownloadReportRecord[] = [];
      const tmpStartDateObjForContract: Date = new Date(1001, 0, 15, 0, 0, 0);
      const tmpEndDateObjForContract: Date = new Date(2098, 0, 15, 0, 0, 0);
      searchedData1.forEach((obj, i) => {
        if(obj.supply_start_date == null){
          if(((s as Date) <= tmpStartDateObjForContract) && ((e as Date) >= tmpEndDateObjForContract)){
            tmpData.push(searchedData1[i]);
          }
        }
        else{
          const dateParts = obj.supply_start_date.split("/");
          const y = parseInt(dateParts[0], 10);
          const m = parseInt(dateParts[1], 10) - 1; // 月は0から始まるため、1を引く
          const d = parseInt(dateParts[2], 10);
          const tmpDateObj: Date = new Date(y, m, d, 0, 0, 0);
          if(tmpDateObj >= (s as Date) && tmpDateObj <= (e as Date)) tmpData.push(searchedData1[i]);
        }
      })
      return tmpData
    }
      
    function searchSupplyEndDate(s: any, e: any){
      let tmpData: AchievementDownloadReportRecord[] = [];
      const tmpStartDateObjForContract: Date = new Date(1001, 0, 15, 0, 0, 0);
      const tmpEndDateObjForContract: Date = new Date(9998, 0, 15, 0, 0, 0);
      searchedData1.forEach((obj, i) => {
        if(obj.contract_end_date == null){
          if(((s as Date) <= tmpStartDateObjForContract) && ((e as Date) >= tmpEndDateObjForContract) ){
            tmpData.push(searchedData1[i]);
          }
        }else{
          const dateParts = obj.contract_end_date.split("/");
          const y = parseInt(dateParts[0], 10);
          const m = parseInt(dateParts[1], 10) - 1; // 月は0から始まるため、1を引く
          const d = parseInt(dateParts[2], 10);
          const tmpDateObj: Date = new Date(y, m, d, 0, 0, 0);
          if(tmpDateObj >= (s as Date) && tmpDateObj <= (e as Date)) tmpData.push(searchedData1[i]);
        }
      })
      return tmpData
    }
  }

  // 検索ボックスのプルダウンに表示するリストを作成する
  const getDropdownSearchList = () => {
    // setterは非同期のため一時配列を用意する
    let tmpNameList: string[] = [];
    let tmpContractIdList: string[] = [];
    let tmpSupplyPointNoList: string[] = [];
    let tmpAddressList: string[] = [];
    let tmpContractTypeNameList: string[] = [];
    
    defaultData.forEach((obj: any, i: Number) => {
      const tmpName: string = obj.name;
      if(tmpNameList.indexOf(tmpName) == -1){
        tmpNameList.push(tmpName);
      }
      const tmpContractId: string = obj.contract_id;
      if(tmpContractIdList.indexOf(tmpContractId) == -1){
        tmpContractIdList.push(tmpContractId);
      }
      if(obj.supply_point_no != null){
        const tmpSupplyPointNo: string = obj.supply_point_no;
        if(tmpSupplyPointNoList.indexOf(tmpSupplyPointNo) == -1){
          tmpSupplyPointNoList.push(tmpSupplyPointNo);
        }
      }
      if(obj.address != null){
        const tmpAddress: string = obj.address;
        if(tmpAddressList.indexOf(tmpAddress) == -1){
          tmpAddressList.push(tmpAddress);
        }
      }
      if(obj.contract_type_name != null){
        const tmpContractTypeName: string = obj.contract_type_name;
        if(tmpContractTypeNameList.indexOf(tmpContractTypeName) == -1){
          tmpContractTypeNameList.push(tmpContractTypeName);
        }
      }
    });
    
    // 更新処理は最後に一括で代入する
    setNameList(tmpNameList);
    setContractIdList(tmpContractIdList);
    setSupplyPointNoList(tmpSupplyPointNoList);
    setAddressList(tmpAddressList);
    setContractTypeNameList(tmpContractTypeNameList);
  }
  
  const resetData = () => {
    setSearchedData([...defaultData]);
    setSearchCondition({...searchCondition, ...resetSearchCondition});
    setResetState(true);
  }
  
  const clearResetSatate = () => {
    setResetState(false);
  }
  
  const pagenation = (curPage: number) => {
    const head = (curPage - 1) * itemsPerPage;
    const targetPage: AchievementDownloadReportRecord[] = searchedData.slice(head, (head + itemsPerPage));
    setCurPageData([...targetPage]);
    setPage(curPage);
  }
  
  const allCheck = (state: boolean) => {
    const updateAllCheck = searchedData.length === defaultData.length?
    defaultData.map((obj) => ({ ...obj, checked: true }))
    : defaultData.map((obj) => {
      const searchedObj = searchedData.find((sObj) => obj["target_ym"] === sObj["target_ym"] && obj["contract_id"] === sObj["contract_id"]);
      return searchedObj ? { ...obj, checked: true } : obj;
    });
    
    const updateAllCancel = searchedData.length === defaultData.length?
    defaultData.map((obj) => ({ ...obj, checked: false }))
    : defaultData.map((obj) => {
      const searchedObj = searchedData.find((sObj) => obj["target_ym"] === sObj["target_ym"] && obj["contract_id"] === sObj["contract_id"]);
      return searchedObj ? { ...obj, checked: false } : obj;
    });
    
    if (state === true){
      setDefaultData(updateAllCheck);
    }
    else if (state === false){
      setDefaultData(updateAllCancel);
    }
  }
  
  const singleCheck = (target_ym: string, contract_id: string) => {
    const updatedSingleCheck = defaultData.map((obj) => {
      if (obj["target_ym"] === target_ym && obj["contract_id"] === contract_id) {
        return { ...obj, checked: !obj["checked"]};
      } else {
        return obj;
      }
    });
    setDefaultData(updatedSingleCheck);
  }

  // サーバーからデータを取得する　
  const getDownloadList: () => Promise<AchievementDownloadReportRecord[] | string> = async () => {
    const token = await getCurrentUserToken();
    let params: { [key: string]: string } = {
      "contractId" : auth.contractID,
      "targetYm" : searchCondition.targetMonth
    }
    if(typeof(auth.requestPointID) !== "undefined") params["requestPointId"] = auth.requestPointID;
    
    const response =axios.request({
      url: baseURL + "/volume-usage/download-monthly-report",
      method: "get",
      headers: { 
        Authorization: `${token}`,
      },
      params : params
    })
    .then((response) => {
      for (let i = 0; i < response.data.data.length; i++) {
        response.data.data[i]["checked"] = false;
      }
      setDefaultData([...response.data.data]);
      if ("contract_name" in response.data){
        return response.data.contract_name;
      }
      return response.data.data;
    })
    .catch((error) => {
      console.log(error);
    });
    
    return response
  };

  return {
    page,
    searchedData,
    curPageData,
    setCurPageData,
    getDownloadList,
    setSearchConditionTargetDate,
    targetDateList,
    setSearchConditionName,
    nameList,
    setSearchConditionContractId,
    contractIdList,
    setSearchConditionSupplyPointNo,
    supplyPointNoList,
    setSearchConditionAddress,
    addressList,
    setSearchConditionContractTypeName,
    contractTypeNameList,
    setSearchConditionVoltageClass,
    setSearchConditionSupplyDate,
    resetState,
    resetData,
    clearResetSatate,
    pagenation,
    allCheck,
    singleCheck,
    initTargetMonth,
    searchCondition
  };
}
